import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { 
  Bus, 
  BookOpen, 
  Laptop, 
  Heart, 
  Users, 
  Gift,
  Shield
} from "lucide-react";

export const FundingCategories = () => {
  const categories = [
    {
      icon: <Bus className="w-8 h-8" />,
      title: "Outdoor Infrastructure",
      color: "bg-blue-100 text-blue-600",
      examples: ["Buses", "Playground upgrades", "Inclusive play structures", "Shade benches"]
    },
    {
      icon: <BookOpen className="w-8 h-8" />,
      title: "Learning Tools",
      color: "bg-purple-100 text-purple-600",
      examples: ["AV equipment", "Books", "Science kits", "Art supplies", "Musical instruments"]
    },
    {
      icon: <Laptop className="w-8 h-8" />,
      title: "Tech Access",
      color: "bg-green-100 text-green-600",
      examples: ["Tablets", "Headphones", "Coding tools", "Educational software subscriptions"]
    },
    {
      icon: <Heart className="w-8 h-8" />,
      title: "Wellness & Inclusion",
      color: "bg-pink-100 text-pink-600",
      examples: ["Snacks", "Hygiene items", "Mental health tools", "Guest counselors"]
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: "Enrichment & Activities",
      color: "bg-orange-100 text-orange-600",
      examples: ["Field trips", "Clubs", "Guest speakers", "Sports gear", "Multicultural events"]
    },
    {
      icon: <Gift className="w-8 h-8" />,
      title: "Community & Appreciation",
      color: "bg-indigo-100 text-indigo-600",
      examples: ["Staff/family events", "School spirit wear", "Classroom wish-list items"]
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: "Emergency Resilience",
      color: "bg-red-100 text-red-600",
      examples: ["Solar + battery systems", "Emergency preparedness", "Safety center capabilities"]
    }
  ];

  return (
    <section className="py-20 bg-gradient-to-br from-gray-50 to-green-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <Badge variant="outline" className="mb-4 text-green-600 border-green-600">
            Eligible PAC Funding
          </Badge>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
            What Your PAC Can Fund
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Energy savings translate directly into comprehensive funding for student programs, 
            infrastructure improvements, and community enrichment initiatives.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {categories.map((category, index) => (
            <Card key={index} className="group hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
              <CardHeader className="pb-4">
                <div className={`w-16 h-16 rounded-2xl ${category.color} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300`}>
                  {category.icon}
                </div>
                <CardTitle className="text-lg font-semibold text-gray-900">
                  {category.title}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  {category.examples.map((example, exampleIndex) => (
                    <li key={exampleIndex} className="text-sm text-gray-600 flex items-center">
                      <div className="w-1.5 h-1.5 bg-green-500 rounded-full mr-2 flex-shrink-0"></div>
                      {example}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="mt-16 bg-white rounded-2xl p-8 shadow-lg">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Energy Financing Criteria
              </h3>
              <p className="text-gray-600 mb-6">
                GreenSmart fully funds installations for PACs that meet our partnership criteria:
              </p>
              <ul className="space-y-3">
                <li className="flex items-center text-gray-700">
                  <div className="w-2 h-2 bg-green-500 rounded-full mr-3"></div>
                  Serve high-density student populations
                </li>
                <li className="flex items-center text-gray-700">
                  <div className="w-2 h-2 bg-green-500 rounded-full mr-3"></div>
                  Located in underserved or emergency-prone zones
                </li>
                <li className="flex items-center text-gray-700">
                  <div className="w-2 h-2 bg-green-500 rounded-full mr-3"></div>
                  Commit to community safety and resilience
                </li>
              </ul>
            </div>
            <div className="bg-gradient-to-br from-green-50 to-blue-50 rounded-xl p-6">
              <h4 className="font-semibold text-gray-900 mb-3">Bonus Benefits</h4>
              <p className="text-gray-600 text-sm mb-4">
                GreenSmart handles CleanBC + federal rebate paperwork and reinvests credits into PAC programs.
              </p>
              <div className="bg-white rounded-lg p-4 shadow-sm">
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-600">8 Years</div>
                  <div className="text-sm text-gray-600">Equipment payoff period</div>
                  <div className="text-xs text-gray-500 mt-1">Then full savings forever</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};